/**
 * Stage 2 — runtime tenant resolution by Host header.
 *
 * Both customer-app and admin-app fetch this on boot to learn which
 * tenant they are serving for the current request. The platform server's
 * Stage 1.5 host-resolution middleware (`packages/core/tenant/host-middleware.ts`)
 * reads the request's Host / X-Forwarded-Host, looks up the matching
 * `TenantDomain` row, and echoes the resolved tenant via
 * `GET /api/v2/tenant/by-host`.
 *
 * Fetch always returns `Promise<TenantHostLookup | null>` — never throws.
 * `null` means "no matching TenantDomain row, run as control-plane
 * default" (per ADR-2026-04-28: ALA is not a tenant, `app.animalloversapp.com`
 * is the control-plane default brand). Callers map non-null lookups onto
 * a baked-in `ClientConfig` for the resolved slug.
 *
 * SECURITY NOTE: tenant lookup is public information — the response
 * carries display name + slug + domain surface, no secrets. Caching this
 * is fine; a stale value just shows the previous tenant's chrome for one
 * navigation, which is a self-correcting UX issue.
 */

const DEFAULT_TIMEOUT_MS = 2000;

/**
 * Mirror of `TenantHostLookup` from
 * `packages/core/tenant/host-middleware.ts`. Duplicated here (rather
 * than imported from `@ferro/core`) because `@ferro/config` must remain
 * a leaf package — `@ferro/core` depends on `@ferro/config`, not the
 * other way around. If the server-side type changes, update this mirror
 * in the same commit (see test for shape contract).
 */
export interface TenantHostLookup {
  tenantId: string;
  slug: string;
  legalName: string;
  displayName: string;
  status: string;
  domain: {
    kind: string;
    surface: string;
    hostname: string;
  };
}

export interface ResolveTenantByHostOptions {
  /**
   * Absolute base URL of the platform API (e.g.
   * `https://animalloversapp-platform-api-production.up.railway.app`).
   * Same value across all tenants — one Railway deploy serves the whole
   * platform. The trailing slash is stripped before concatenation.
   */
  apiBaseUrl: string;
  /**
   * Hostname the page is served on (typically `window.location.hostname`).
   * Sent as `?host=` query so the platform API can resolve the tenant
   * even though the API is on a different origin than the page —
   * cross-origin browser fetches don't naturally carry the page's host
   * to the API, so we pass it explicitly. Server-side: query param takes
   * precedence over X-Forwarded-Host / Host. Omit to fall back to
   * server-side header detection (legacy callers, same-origin SSR).
   */
  host?: string;
  /** Test seam — defaults to global fetch. */
  fetchFn?: typeof fetch;
  /** Test seam — defaults to 2000ms (boot must not block longer). */
  timeoutMs?: number;
}

/**
 * #241 — discriminated resolve outcome. The flat `TenantHostLookup | null`
 * contract collapses two VERY different situations into one value:
 *
 *   - "the platform answered and this host has no tenant" (definitive —
 *     the control-plane/ALA fallback is CORRECT), and
 *   - "the platform could not be reached / errored / timed out"
 *     (transient — cold start, CORS-dropped error response, network
 *     blip — committing to the ALA fallback here wrong-brands the
 *     whole surface until the next full reload).
 *
 * Callers that need to retry (pickTenantConfig in both SPAs) use
 * `resolveTenantByHostDetailed` and only retry when `retryable` is true.
 */
export type TenantResolveOutcome =
  | { ok: true; tenant: TenantHostLookup | null }
  | {
      ok: false;
      /**
       * Transient failures (network, timeout, 5xx, 408, 429) are
       * retryable. Definitive 4xx answers (400/403/404/410 — wrong API
       * base, WAF block, host-gate hard-reject) and malformed 200
       * bodies are NOT: they will not change on retry, so retrying
       * only burns the boot budget (#247 review, requirement d).
       */
      retryable: boolean;
      reason: 'no_fetch' | 'network_error' | 'http_error' | 'bad_shape';
      /** HTTP status when reason === 'http_error'. */
      status?: number;
    };

/**
 * Issue the bootstrap fetch and report a discriminated outcome. Never
 * throws. See `TenantResolveOutcome` for the contract.
 */
export async function resolveTenantByHostDetailed(
  opts: ResolveTenantByHostOptions,
): Promise<TenantResolveOutcome> {
  const fetchFn = opts.fetchFn ?? globalThis.fetch;
  if (typeof fetchFn !== 'function') {
    return { ok: false, retryable: false, reason: 'no_fetch' };
  }

  const timeoutMs = opts.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const baseUrl = opts.apiBaseUrl.replace(/\/+$/, '');
  const url = opts.host
    ? `${baseUrl}/api/v2/tenant/by-host?host=${encodeURIComponent(opts.host)}`
    : `${baseUrl}/api/v2/tenant/by-host`;

  const controller =
    typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = controller
    ? setTimeout(() => controller.abort(), timeoutMs)
    : null;

  try {
    const res = await fetchFn(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      ...(controller ? { signal: controller.signal } : {}),
    });
    if (timer) clearTimeout(timer);

    // 5xx is retryable: 503 tenant_lookup_unavailable (cold Neon, see
    // apps/server by-host route), Railway edge 502/503 while the
    // container wakes, transient 500s from the host-gate. 408 (request
    // timeout) and 429 (rate limited) are the only retryable 4xx —
    // every other 4xx is a DEFINITIVE answer (wrong apiBaseUrl 404,
    // WAF 403, host-gate hard-reject) that a retry cannot change.
    if (!res.ok) {
      return {
        ok: false,
        retryable:
          res.status >= 500 || res.status === 408 || res.status === 429,
        reason: 'http_error',
        status: res.status,
      };
    }

    let data: { tenant?: unknown };
    try {
      data = (await res.json()) as { tenant?: unknown };
    } catch {
      // A 200 whose body doesn't parse is a contract break, not a
      // network blip — same non-retryable classification as bad_shape.
      return { ok: false, retryable: false, reason: 'bad_shape' };
    }
    const tenant = data.tenant;
    // `tenant: null` (or absent) on a 200 is the DEFINITIVE "no tenant
    // for this host" answer — bare admin console, localhost, previews.
    if (tenant == null) return { ok: true, tenant: null };
    if (typeof tenant !== 'object' || !isTenantHostLookup(tenant)) {
      // 200 with an unexpected body — a contract break, not a blip.
      return { ok: false, retryable: false, reason: 'bad_shape' };
    }
    return { ok: true, tenant };
  } catch {
    // Network failure, CORS-masked error response, or abort (timeout) —
    // all transient from the SPA's point of view.
    if (timer) clearTimeout(timer);
    return { ok: false, retryable: true, reason: 'network_error' };
  }
}

/**
 * Issue the bootstrap fetch and return the resolved tenant, or `null`
 * if the host doesn't match a TenantDomain row, the request fails, or
 * the response shape is unexpected. Never throws.
 *
 * NOTE (#241): this flat shape cannot distinguish "definitively no
 * tenant" from "lookup failed". Boot-critical callers should use
 * `resolveTenantByHostDetailed` and retry on retryable failures.
 */
export async function resolveTenantByHost(
  opts: ResolveTenantByHostOptions,
): Promise<TenantHostLookup | null> {
  const outcome = await resolveTenantByHostDetailed(opts);
  return outcome.ok ? outcome.tenant : null;
}

function isTenantHostLookup(v: object): v is TenantHostLookup {
  const o = v as Record<string, unknown>;
  if (typeof o.tenantId !== 'string') return false;
  if (typeof o.slug !== 'string') return false;
  if (typeof o.legalName !== 'string') return false;
  if (typeof o.displayName !== 'string') return false;
  if (typeof o.status !== 'string') return false;
  if (!o.domain || typeof o.domain !== 'object') return false;
  const d = o.domain as Record<string, unknown>;
  if (typeof d.kind !== 'string') return false;
  if (typeof d.surface !== 'string') return false;
  if (typeof d.hostname !== 'string') return false;
  return true;
}
